// App.tsx — Root of the Farstars SPA. Loads the player's galaxy from the SSO-gated
// session API (falling back to a locally-generated galaxy with no backend), owns
// the focus hierarchy (galaxy → system → planet), and composes the full-screen 3D
// <Canvas> with the 2D HUD overlay. It is one continuous map in true coordinates.
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { ACESFilmicToneMapping } from 'three';
import { generateGalaxy } from './galaxy/generate';
import { AuthRequiredError, connectSim, fetchLoginCapabilities, fetchMe, fetchSession, getMatch } from './api';
import type { LoginCapabilities, MatchView, Player, SimController } from './api';
import { LoginOverlay } from './LoginOverlay';
import { GameContext, MatchContext, SimContext } from './state/context';
import type { GameState, MatchState } from './state/context';
import { galaxyScope } from './state/scope';
import { Lobby, MatchRoom } from './ui/Lobby';
import type { SimState } from './state/economy';
import { anchorClock, clockAnchor } from './state/gameClock';
import { focusParent, resolveFocus } from './state/focus';
import type { Galaxy as GalaxyData } from './types';
import { Background } from './scene/Background';
import { AutoExposure } from './scene/AutoExposure';
import { CameraRig } from './scene/CameraRig';
import { GameClock } from './scene/GameClock';
import { PointerSync } from './scene/PointerSync';
import { Galaxy } from './scene/Galaxy';
import { Telemetry } from './scene/Telemetry';
import { E2EBridge } from './scene/E2EBridge';
import { Hud } from './ui/Hud';
import { E2E_ENABLED, e2eAssign } from './e2e';

const LOCAL_SEED = 0x2f1a55;

// What the page is showing: the game browser, a match's waiting room, a live match, or
// the empire-less sandbox galaxy (the original living-economy view). Picked from the URL.
type Mode = 'lobby' | 'room' | 'match' | 'sandbox';
const PARAMS = new URLSearchParams(window.location.search);
const MATCH_ID = PARAMS.get('match');
const SANDBOX = PARAMS.has('sandbox');

export default function App() {
  const [mode, setMode] = useState<Mode>(SANDBOX ? 'sandbox' : MATCH_ID ? 'room' : 'lobby');
  const [matchView, setMatchView] = useState<MatchView | null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [bootError, setBootError] = useState('');
  const [galaxy, setGalaxy] = useState<GalaxyData | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);
  // One focus slot: the id of the deepest focused entity (system / planet / gate),
  // or null = galaxy. The typed views are derived from it (state/focus.ts).
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedShipId, setSelectedShipId] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);
  // Latest server economy snapshot (ships + pops). Null before the first snapshot and
  // in offline dev (no backend); kept in its own SimContext so the ~220 StarMarkers
  // don't re-render on every snapshot.
  const [sim, setSim] = useState<SimState | null>(null);
  const simCtl = useRef<SimController | null>(null);
  // Non-null once /api/session has answered 401: the galaxy keeps rendering
  // underneath while the sign-in panel sits over it. Signing in used to mean
  // leaving for the authz domain and losing that view entirely.
  const [loginCaps, setLoginCaps] = useState<LoginCapabilities | null>(null);
  // Bumped after a successful sign-in to re-run the session bootstrap.
  const [sessionEpoch, setSessionEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const onAuthError = (err: unknown): boolean => {
      if (!(err instanceof AuthRequiredError)) return false;
      // Ask what sign-in this deployment supports, then render the panel over the galaxy
      // rather than navigating away for it.
      void fetchLoginCapabilities().then((caps) => {
        if (!cancelled) setLoginCaps(caps);
      });
      setGalaxy((g) => g ?? generateGalaxy(LOCAL_SEED));
      return true;
    };
    if (SANDBOX) {
      fetchSession()
        .then((session) => {
          if (cancelled) return;
          setGalaxy(session.galaxy);
          setPlayer(session.player);
          setLoginCaps(null);
        })
        .catch((err: unknown) => {
          if (cancelled || onAuthError(err)) return;
          console.warn('farstars: session unavailable, using local galaxy', err);
          setGalaxy(generateGalaxy(LOCAL_SEED));
          setPlayer(null);
        });
    } else {
      // Lobby / match: sign in, then (for a match) load its shared galaxy. The lobby and the
      // waiting room float over a decorative local galaxy.
      fetchMe()
        .then(async (me) => {
          if (cancelled) return;
          setPlayer(me);
          setLoginCaps(null);
          if (!MATCH_ID) {
            setGalaxy(generateGalaxy(LOCAL_SEED));
            setMode('lobby');
            return;
          }
          const m = await getMatch(MATCH_ID, true);
          if (cancelled) return;
          setMatchView(m);
          if (m.status === 'lobby' || !m.galaxy) {
            setGalaxy(generateGalaxy(LOCAL_SEED));
            setMode('room');
          } else {
            galaxyScope.match = m.id;
            setGalaxy(m.galaxy);
            setMode('match');
          }
        })
        .catch((err: unknown) => {
          if (cancelled || onAuthError(err)) return;
          setGalaxy((g) => g ?? generateGalaxy(LOCAL_SEED));
          setBootError(err instanceof Error ? err.message : 'Could not reach the server.');
          setMode('lobby');
        });
    }
    return () => {
      cancelled = true;
    };
  }, [sessionEpoch]);

  // Once we have an authenticated session, open the authoritative game-state stream:
  // each snapshot updates the economy and re-anchors the (now server-driven) clock.
  useEffect(() => {
    if (!player || (mode !== 'sandbox' && mode !== 'match')) return;
    const path = mode === 'match' && matchView ? `/api/matches/${encodeURIComponent(matchView.id)}/state` : '/api/state';
    const ctl = connectSim((state) => {
      setSim(state);
      anchorClock(state.gameTime, state.speed);
    }, path);
    simCtl.current = ctl;
    return () => {
      ctl.close();
      simCtl.current = null;
    };
  }, [player, mode, matchView]);

  // Opening a match: fly to your capital once the first snapshot names it.
  const flewHome = useRef(false);
  useEffect(() => {
    if (flewHome.current || mode !== 'match' || !sim || !matchView?.you || !galaxy) return;
    const me = (sim.empires ?? []).find((e) => e.id === matchView.you);
    const sys = me && galaxy.systems.find((s) => s.planets.some((p) => p.id === me.capital));
    if (sys) {
      flewHome.current = true;
      setFocusedId(sys.id);
    }
  }, [mode, sim, matchView, galaxy]);

  const match: MatchState | null = useMemo(() => {
    if (mode !== 'match' || !matchView) return null;
    return {
      matchId: matchView.id,
      name: matchView.name,
      you: matchView.you,
      isHost: matchView.isHost,
      baseSpeed: matchView.baseSpeed,
      issue: async (cmd) => {
        const ctl = simCtl.current;
        if (!ctl) return 'not connected';
        const r = await ctl.command(cmd);
        return r.ok ? null : r.error ?? 'order rejected';
      },
      selection,
      setSelection,
    };
  }, [mode, matchView, selection]);

  // Speed is server-authoritative: tell the server, optimistically re-anchor so the
  // clock speeds up immediately, and keep the local value for the button highlight.
  const changeSpeed = useCallback((s: number) => {
    setSpeed(s);
    // In a match only the host's request takes effect (the server ignores the rest), so
    // only re-anchor optimistically when it will.
    if (mode !== 'match' || matchView?.isHost) clockAnchor.speed = s;
    simCtl.current?.setSpeed(s);
  }, [mode, matchView]);

  // Derive the typed by-kind views from the single focused id.
  const focus = useMemo(() => resolveFocus(galaxy, focusedId), [galaxy, focusedId]);
  const {
    systemId: focusedSystemId,
    planetId: focusedPlanetId,
    moonId: focusedMoonId,
    singularityId: selectedSingularityId,
  } = focus;

  // Following a selected ship: keep the view on whatever layer it is on — its system while
  // it flies or holds orbit there, the galaxy map while it crosses a hyperlane — and drop the
  // selection if it is destroyed or used up (a colony ship founding its colony).
  useEffect(() => {
    if (!sim || !selectedShipId || !galaxy) return;
    const ship = (sim.ships ?? []).find((s) => s.id === selectedShipId);
    if (!ship) {
      setSelectedShipId(null);
      return;
    }
    if (ship.phase === 'hyperlane') {
      if (focusedId !== null) setFocusedId(null);
    } else if (ship.systemId !== focusedSystemId) {
      setFocusedId(ship.systemId);
    }
  }, [sim, selectedShipId, galaxy, focusedId, focusedSystemId]);


  // The server simulates the whole galaxy but streams economy detail only for the system
  // we're viewing — tell it whenever that changes (the controller resends on reconnect).
  useEffect(() => {
    simCtl.current?.setFocus(focusedSystemId);
  }, [focusedSystemId]);

  // Test-only: expose the live ships so the playtest can verify they stream + render.
  useEffect(() => {
    if (!E2E_ENABLED) return;
    e2eAssign({
      getSimShips: () =>
        (sim?.ships ?? []).map((s) => ({ id: s.id, systemId: s.systemId, phase: s.phase, originId: s.originId, destId: s.destId, posLocal: s.posLocal })),
    });
  }, [sim]);

  // Focus actions (planet/star/gate) deselect any selected ship; selecting a ship can
  // itself focus that ship's system (so an in-system ship clicked from the galaxy flies in).
  const focusEntity = useCallback((id: string | null) => {
    setSelectedShipId(null);
    setFocusedId(id);
  }, []);
  const focusUp = useCallback(() => {
    setSelectedShipId(null);
    setFocusedId((id) => focusParent(galaxy, id));
  }, [galaxy]);
  const selectShip = useCallback((id: string | null, systemId?: string) => {
    if (id && systemId) setFocusedId(systemId);
    setSelectedShipId(id);
  }, []);

  const game: GameState = useMemo(
    () => ({
      focusedId,
      focusedSystemId,
      focusedPlanetId,
      focusedMoonId,
      selectedSingularityId,
      hoveredId,
      selectedShipId,
      selectShip,
      speed,
      focus: focusEntity,
      focusUp,
      hover: setHoveredId,
      setSpeed: changeSpeed,
    }),
    [focusedId, focusedSystemId, focusedPlanetId, focusedMoonId, selectedSingularityId, hoveredId, selectedShipId, speed, focusEntity, focusUp, selectShip, changeSpeed],
  );

  // Publish the playtest control surface (no-op without ?e2e=1; see ./e2e.ts). The
  // by-kind action names are kept as aliases of the single focus() for test compat.
  useEffect(() => {
    if (!E2E_ENABLED) return;
    e2eAssign({
      focus: game.focus,
      // By-kind aliases preserve the old per-action null semantics: focusSystem(null)
      // drops to the galaxy, while focusPlanet/selectSingularity(null) step UP one
      // level (back to the system), not all the way out.
      focusSystem: (id) => game.focus(id),
      focusPlanet: (id) => (id ? game.focus(id) : game.focusUp()),
      selectSingularity: (id) => (id ? game.focus(id) : game.focusUp()),
      selectShip: setSelectedShipId,
      hover: game.hover,
    });
  }, [game]);
  useEffect(() => {
    if (!E2E_ENABLED || !galaxy) return;
    e2eAssign({
      systems: galaxy.systems,
      hyperlanes: galaxy.hyperlanes,
      getState: () => ({
        focusedId,
        focusedSystemId,
        focusedPlanetId,
        focusedMoonId,
        selectedSingularityId,
        hoveredId,
      }),
      ready: true,
    });
  }, [galaxy, focusedId, focusedSystemId, focusedPlanetId, focusedMoonId, selectedSingularityId, hoveredId]);

  if (!galaxy) {
    return <div className="boot">Charting the galaxy&hellip;</div>;
  }

  const focusedSystem = focusedSystemId
    ? galaxy.systems.find((s) => s.id === focusedSystemId) ?? null
    : null;

  const playing = mode === 'match' || mode === 'sandbox';
  return (
    <GameContext.Provider value={game}>
      <MatchContext.Provider value={match}>
      <SimContext.Provider value={sim}>
      <div className="viewport">
        <Canvas
          dpr={1}
          gl={{ antialias: true, toneMapping: ACESFilmicToneMapping }}
          camera={{ position: [2, 120, 1], fov: 50, near: 0.03, far: 2000 }}
        >
          <Suspense fallback={null}>
            <Background />
            <Galaxy galaxy={galaxy} />
          </Suspense>
          <CameraRig
            systems={galaxy.systems}
            focusedSystemId={focusedSystemId}
            focusedPlanetId={focusedPlanetId}
            focusedMoonId={focusedMoonId}
            selectedSingularityId={selectedSingularityId}
            followShipId={selectedShipId}
          />
          <AutoExposure systems={galaxy.systems} focusedPlanetId={focusedPlanetId} />
          <GameClock />
          <PointerSync />
          <Telemetry />
          {E2E_ENABLED ? <E2EBridge /> : null}
        </Canvas>
        {playing ? (
          <Hud
            galaxy={galaxy}
            focusedSystem={focusedSystem}
            focusedPlanetId={focusedPlanetId}
            player={player}
          />
        ) : null}
        {!loginCaps && player && mode === 'lobby' ? <Lobby /> : null}
        {!loginCaps && player && mode === 'room' && matchView ? <MatchRoom initial={matchView} /> : null}
        {bootError && !loginCaps ? <div className="boot-error">{bootError}</div> : null}
        {loginCaps && (
          <LoginOverlay
            caps={loginCaps}
            onSignedIn={() => {
              setLoginCaps(null);
              setSessionEpoch((n) => n + 1);
            }}
          />
        )}
      </div>
      </SimContext.Provider>
      </MatchContext.Provider>
    </GameContext.Provider>
  );
}
